import { useIsomorphicLayoutEffect } from '@repo/common-utils';
import { openDB } from 'idb';
import { useAtom } from 'jotai';
import { useEffect } from 'react';

import { MAX_SAVE_PERIOD } from '@/lib/constants/chat-history';
import { LOADED_CONTENT_OBJECT_STORE } from '@/lib/constants/storage-keys';
import { seanWebDbAtom } from '@/lib/stores/platform';

export const useSenaWebDb = () => {
  const [senaDb, setSenaDb] = useAtom(seanWebDbAtom);
  const currentVersion = 4;

  const getAllChatContents = async () => {
    if (!senaDb || !senaDb.objectStoreNames.contains(LOADED_CONTENT_OBJECT_STORE)) return [];
    return senaDb.getAll(LOADED_CONTENT_OBJECT_STORE);
  };

  const getChatContent = (chatId: string) => {
    if (!senaDb || !senaDb.objectStoreNames.contains(LOADED_CONTENT_OBJECT_STORE)) return null;

    return senaDb.get(LOADED_CONTENT_OBJECT_STORE, chatId);
  };

  const setChatContent = async (sessionId: string, chatId: string, content: unknown) => {
    if (!senaDb || !senaDb.objectStoreNames.contains(LOADED_CONTENT_OBJECT_STORE)) return;

    const prev = await senaDb.get(LOADED_CONTENT_OBJECT_STORE, chatId);

    return senaDb.put(LOADED_CONTENT_OBJECT_STORE, {
      sessionId,
      chatId,
      content,
      updatedAt: new Date().toISOString(),
      isPinned: prev?.isPinned ?? false
    });
  };

  const deleteChatContent = async (chatId: string) => {
    if (!senaDb || !senaDb.objectStoreNames.contains(LOADED_CONTENT_OBJECT_STORE)) return;

    return senaDb.delete(LOADED_CONTENT_OBJECT_STORE, chatId);
  };

  const updateChatPinned = async (chatId: string, isPinned: boolean) => {
    if (!senaDb || !senaDb.objectStoreNames.contains(LOADED_CONTENT_OBJECT_STORE)) return;
    const chat = await senaDb.get(LOADED_CONTENT_OBJECT_STORE, chatId);
    if (!chat) return;
    chat.isPinned = isPinned;
    chat.updatedAt = new Date().toISOString();
    return senaDb.put(LOADED_CONTENT_OBJECT_STORE, chat);
  };

  const getPinnedChatContents = async () => {
    if (!senaDb || !senaDb.objectStoreNames.contains(LOADED_CONTENT_OBJECT_STORE)) return [];

    try {
      const allChats = await senaDb.getAll(LOADED_CONTENT_OBJECT_STORE);
      return allChats.filter((chat) => chat.isPinned);
    } catch (error) {
      console.error('Error getting pinned chats:', error);
      return [];
    }
  };

  useIsomorphicLayoutEffect(() => {
    if (senaDb) return;

    (async () => {
      let backupData: any[] = [];
      const dbName = 'sena-web-db';
      // 먼저 기존 데이터를 백업
      const tempDb = await openDB(dbName);
      if (tempDb.objectStoreNames.contains(LOADED_CONTENT_OBJECT_STORE) && tempDb.version < currentVersion) {
        const tx = tempDb.transaction(LOADED_CONTENT_OBJECT_STORE, 'readonly');
        const store = tx.objectStore(LOADED_CONTENT_OBJECT_STORE);
        backupData = await store.getAll();
        await tx.done;
      }
      tempDb.close();

      const dbInstance = await openDB(dbName, currentVersion, {
        upgrade(db, oldVersion) {
          if (db.objectStoreNames.contains('preloadedChatContent')) {
            db.deleteObjectStore('preloadedChatContent');
          }

          if (oldVersion < currentVersion && db.objectStoreNames.contains(LOADED_CONTENT_OBJECT_STORE)) {
            db.deleteObjectStore(LOADED_CONTENT_OBJECT_STORE);
          }
          const store = db.createObjectStore(LOADED_CONTENT_OBJECT_STORE, { keyPath: 'chatId' });
          store.createIndex('sessionId', 'sessionId');
          store.createIndex('updatedAt', 'updatedAt');
          store.createIndex('isPinned', 'isPinned');
        }
      });

      // 백업 데이터 복원 (isPinned 기본값 false 적용)
      if (backupData.length > 0) {
        const tx = dbInstance.transaction(LOADED_CONTENT_OBJECT_STORE, 'readwrite');
        const store = tx.objectStore(LOADED_CONTENT_OBJECT_STORE);
        for (const item of backupData) {
          if (item.isPinned === undefined) item.isPinned = false;
          await store.put(item);
        }
        await tx.done;
      }

      setSenaDb(dbInstance);
    })();
  }, []);

  useEffect(() => {
    // 30일 이상 지난 데이터 중 isPinned가 false인 것만 삭제
    if (!senaDb || !senaDb.objectStoreNames.contains(LOADED_CONTENT_OBJECT_STORE)) return;

    const tx = senaDb.transaction(LOADED_CONTENT_OBJECT_STORE, 'readwrite');
    const store = tx.objectStore(LOADED_CONTENT_OBJECT_STORE);
    const index = store.index('updatedAt');
    const since = new Date(Date.now() - MAX_SAVE_PERIOD * 24 * 60 * 60 * 1000).toISOString();

    (async () => {
      for await (const cursor of index.iterate(IDBKeyRange.upperBound(since, true))) {
        if (!cursor.value.isPinned) {
          await cursor.delete();
        }
      }
    })();
  }, [senaDb]);

  return {
    getChatContent,
    setChatContent,
    deleteChatContent,
    isReady: !!senaDb && senaDb.objectStoreNames.contains(LOADED_CONTENT_OBJECT_STORE),
    getAllChatContents,
    updateChatPinned,
    getPinnedChatContents
  };
};
